import React, { useState } from 'react'
import {
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Collapse,
  ListItemIcon,
  ListItemButton,
  useTheme,
} from '@mui/material'
import {
  ExpandLess,
  ExpandMore,
  Folder,
  Description,
} from '@mui/icons-material'
import { IMenu } from '@/types'
import { t } from '@/i18n'

interface MenuSectionProps {
  title: string
  items?: IMenu[]
  sections?: MenuSectionProps[]
  onItemClick: (path: string, name: string) => void
  disabled?: boolean
  level?: number
}

const MenuSection: React.FC<MenuSectionProps> = ({
  title,
  items = [],
  sections = [],
  onItemClick,
  disabled = false,
  level = 0,
}) => {
  const theme = useTheme()
  const [open, setOpen] = useState(true)
  const hasChildren = sections.length > 0 || items.length > 0
  const isRoot = level === 0

  return (
    <Box sx={{ mb: 1 }}>
      <ListItemButton
        onClick={() => setOpen(!open)}
        sx={{
          pl: level * 2,
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          backgroundColor: isRoot ? theme.palette.grey[100] : 'transparent',
          borderBottom: isRoot ? `1px solid ${theme.palette.divider}` : 'none',
          direction: 'rtl',
          textAlign: 'right',
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          {hasChildren ? (
            <Folder fontSize="small" />
          ) : (
            <Description fontSize="small" />
          )}
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography
              variant={isRoot ? 'subtitle1' : 'body1'}
              sx={{
                fontWeight: isRoot ? 'bold' : 'normal',
                color: isRoot ? theme.palette.primary.main : 'inherit',
              }}
            >
              {t(title)}
            </Typography>
          }
        />
        {hasChildren && (
          <ListItemIcon sx={{ minWidth: 'auto' }}>
            {open ? <ExpandLess /> : <ExpandMore />}
          </ListItemIcon>
        )}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding dense>
          {sections.map((section, index) => (
            <MenuSection
              key={`${section.title}-${index}`}
              {...section}
              onItemClick={onItemClick}
              disabled={disabled}
              level={level + 1}
            />
          ))}
          {items.map(item => (
            <ListItem
              key={item.id}
              button
              disabled={disabled}
              onClick={() => item.path && onItemClick(item.path, item.name)}
              sx={{
                pl: (level + 1) * 2,
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                direction: 'rtl',
                textAlign: 'right',
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Description fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t(item.name)}
                sx={{
                  '& .MuiTypography-root': {
                    fontSize: '0.875rem',
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Box>
  )
}

export default MenuSection 